<template>
    <Teleport to="body">
        <Transition name="drawer">
            <div v-if="open" class="drawer-overlay" @mousedown.self="requestClose">
                <aside
                    ref="panelRef"
                    class="drawer"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="titleId"
                    tabindex="-1"
                    @keydown.esc.stop="requestClose"
                >
                    <header class="drawer__header">
                        <div class="drawer__heading">
                            <h2 :id="titleId" class="drawer__title">{{ title }}</h2>
                            <p v-if="subtitle" class="drawer__subtitle">{{ subtitle }}</p>
                        </div>
                        <button
                            type="button"
                            class="drawer__close"
                            aria-label="Закрыть"
                            :disabled="busy"
                            @click="requestClose"
                        >&times;</button>
                    </header>

                    <div class="drawer__body">
                        <slot />
                    </div>

                    <footer v-if="$slots.footer" class="drawer__footer">
                        <slot name="footer" />
                    </footer>
                </aside>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
    open: { type: Boolean, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    busy: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const panelRef = ref(null);
const titleId = `drawer-title-${Math.random().toString(36).slice(2, 9)}`;

const requestClose = () => {
    if (!props.busy) emit('close');
};

const setBodyScrollLocked = (isLocked) => {
    document.body.style.overflow = isLocked ? 'hidden' : '';
};

watch(() => props.open, async (isOpen) => {
    setBodyScrollLocked(isOpen);
    if (!isOpen) return;

    await nextTick();
    const firstField = panelRef.value?.querySelector('input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
    (firstField || panelRef.value)?.focus();
}, { immediate: true });

onBeforeUnmount(() => setBodyScrollLocked(false));
</script>

<style scoped lang="scss">
@use '../../../scss/pages/admin/admin-tokens' as *;

.drawer-overlay {
    position: fixed;
    inset: 0;
    z-index: $z-drawer;
    display: flex;
    justify-content: flex-end;
    background: rgba(17, 24, 39, 0.45);
}

.drawer {
    display: flex;
    flex-direction: column;
    width: min(560px, 100vw);
    height: 100%;
    background: $admin-bg-primary;
    box-shadow: -12px 0 32px rgba(0, 0, 0, 0.15);
    outline: none;
}

.drawer__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $spacing-lg;
    padding: $spacing-xl;
    border-bottom: 1px solid $admin-border-color;
}

.drawer__title {
    margin: 0;
    font-size: $font-size-2xl;
    font-weight: 600;
    color: $admin-text-primary;
}

.drawer__subtitle {
    margin: $spacing-xs 0 0;
    font-size: $font-size-sm;
    color: $admin-text-muted;
    word-break: break-all;
}

.drawer__close {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: $radius-sm;
    background: none;
    font-size: 1.75rem;
    line-height: 1;
    color: $admin-text-muted;
    cursor: pointer;

    &:hover { background: $admin-bg-hover; color: $admin-text-primary; }
}

.drawer__body {
    flex: 1;
    overflow-y: auto;
    padding: $spacing-xl;
}

.drawer__footer {
    display: flex;
    justify-content: flex-end;
    gap: $spacing-md;
    padding: $spacing-lg $spacing-xl;
    border-top: 1px solid $admin-border-color;
    background: $admin-bg-secondary;
}

.drawer-enter-active,
.drawer-leave-active {
    transition: opacity 0.2s ease;

    .drawer { transition: transform 0.25s ease; }
}

.drawer-enter-from,
.drawer-leave-to {
    opacity: 0;

    .drawer { transform: translateX(100%); }
}
</style>
